import { CheckCircle, Info, Warning, XCircle, X } from "@phosphor-icons/react";
import { toast as sonnerToast } from "sonner";

type ToastType = "success" | "info" | "warning" | "error";

interface ToastProps {
  id: string | number;
  type?: ToastType;
  title?: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}

const icons: Record<ToastType, React.ReactNode> = {
  success: <CheckCircle className="size-5 text-emerald-600" weight="fill" />,
  info: <Info className="size-5 text-blue-600" weight="fill" />,
  warning: <Warning className="size-5 text-amber-600" weight="fill" />,
  error: <XCircle className="size-5 text-red-600" weight="fill" />,
};

function Toast({
  id,
  type = "success",
  title,
  description,
  action,
}: ToastProps) {
  return (
    <div className="flex w-[364px] items-center gap-3 rounded-lg bg-white p-4 shadow-lg ring-1 ring-black/5">
      <div className="shrink-0">{icons[type]}</div>
      <div className="flex-1 min-w-0">
        {title && <p className="text-sm font-medium text-gray-900">{title}</p>}
        {description && (
          <p className={`text-sm text-gray-500 ${title ? "mt-0.5" : ""}`}>
            {description}
          </p>
        )}
      </div>
      {action && (
        <button
          className="shrink-0 rounded bg-gray-100 px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-200"
          onClick={() => {
            action.onClick();
            sonnerToast.dismiss(id);
          }}
        >
          {action.label}
        </button>
      )}
      <button
        type="button"
        aria-label="Dismiss notification"
        className="shrink-0 rounded-full p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
        onClick={() => sonnerToast.dismiss(id)}
      >
        <X className="size-4" weight="bold" />
      </button>
    </div>
  );
}

interface ShowToastOptions {
  title?: string;
  description?: string;
  type?: ToastType;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

function toast(options: ShowToastOptions) {
  const { duration, ...rest } = options;
  return sonnerToast.custom((id) => <Toast id={id} {...rest} />, { duration });
}

toast.success = (
  title: string,
  opts?: Omit<ShowToastOptions, "title" | "type">,
) => toast({ title, type: "success", ...opts });

toast.error = (
  title: string,
  opts?: Omit<ShowToastOptions, "title" | "type">,
) => toast({ title, type: "error", ...opts });

toast.warning = (
  title: string,
  opts?: Omit<ShowToastOptions, "title" | "type">,
) => toast({ title, type: "warning", ...opts });

toast.info = (title: string, opts?: Omit<ShowToastOptions, "title" | "type">) =>
  toast({ title, type: "info", ...opts });

toast.dismiss = sonnerToast.dismiss;

export { toast, Toast, type ShowToastOptions, type ToastType };
